/**
 * Nav-link adapter for CodeStitch top-level header navigation items.
 *
 * V1 intentionally supports only desktop/horizontal top-level
 * <ul class="cs-ul"> > <li class="cs-li"> links as drag sources.
 * Dropdown roots participate as sibling targets so the row makes room around
 * them, but dropdown roots and dropdown sub-items are not draggable handles.
 */

import { moveSiblingPreservingEids } from '../element-insert.js';

export const navLinkAdapter = {
  kind: 'nav-link',
  label: 'Nav link',
  itemSelector: 'li.cs-li',
  parentSelector: 'ul.cs-ul',
  // Phase 23: include dropdown roots as drag handles. Sub-item clicks (inside
  // ul.cs-drop-ul) are filtered by a dedicated guard in iframe-bridge's
  // _findReorderHandle so they do not accidentally drag the parent dropdown.
  reorderSelector: 'ul.cs-ul > li.cs-li',
  reorderSiblingSelector: 'ul.cs-ul > li.cs-li',
  // Region scope: the broad `ul.cs-ul > li.cs-li` selector also matches footer
  // link lists (which use the same classes). Constrain nav-link drags to the
  // header/nav region so a footer link never triggers the nav reorder path.
  requiredAncestorSelector: 'header, nav.cs-nav',
  // Phase 23: dropdown sub-items (inside ul.cs-drop-ul) must not hijack the
  // parent dropdown as a drag source. Clicks on the dropdown ROOT's own
  // children are NOT inside ul.cs-drop-ul, so dragging the dropdown from its
  // root remains available.
  rejectIfTargetClosest: 'ul.cs-drop-ul',
  // Reorder contract (Phase 1): axis drives hit-testing/CSS; historyLabel is the
  // undo/surgical-sync key AND the View Transition key; vtPrefix/vtSelector stamp
  // view-transition names; moveFn is the parent-side DOM mover (never serialized
  // to the iframe — getReorderableSpecs emits string fields only).
  reorderAxis: 'x',
  historyLabel: 'Reorder nav link',
  toastText: 'Nav link reordered',
  vtPrefix: 'pb-nav-',
  vtSelector: 'ul.cs-ul > li.cs-li',
  moveFn: moveSiblingPreservingEids,
  menuActions: ['duplicate', 'delete'],
  postInsertFlow: null,
  cssPatch: null,
  reorderable: true,

  match(probe) {
    if (!probe || typeof probe.closest !== 'function') return null;
    if (probe.closest('ul.cs-drop-ul')) return null;
    // A footer link list uses the same li.cs-li idiom — it belongs to the
    // footer-link adapter, not nav. (The drag path is already region-scoped via
    // requiredAncestorSelector; this keeps context-menu classification correct.)
    if (probe.closest('footer')) return null;
    const li = probe.closest('li.cs-li');
    if (!li) return null;
    const classNames = Array.isArray(li.classNames)
      ? li.classNames
      : li.classList
        ? Array.from(li.classList)
        : [];
    if (classNames.map((c) => String(c).toLowerCase()).includes('cs-dropdown')) return null;
    return li;
  }
};
